<template>
  <div style="min-height: calc(100vh - 158px);" class="card">
    <div style="background: #DBE7F2" class="card p-2">
      <TitleButton
          :showBtn="false"
          :showAddNew="false"
          title="Sales Book(Mushak 6.2)"
      />

      <div class="row mt-2">
        <div class="col-md-4">
          <label for="colFormLabel">
              Product/Service Type
          </label>
          <v-select
              placeholder="Select Product/Service Type"
              v-model="productType"
              :options="productTypes"
              label="text"
              :reduce="text => text.id"
          />
        </div>
        <div class="col-md-4">
          <label for="colFormLabel">
              Product/Service
          </label>
          <AsyncSelect
            ref="productSelect"
            placeholder="Select Product/Service"
            :api-service="fetchProductList"
            v-model="product"
            label="text"
            :format-label="option => option.text"
            :additional-query = "{
              product_types: productType
            }"
          />
        </div>
        <div class="col-md-4">
          <label for="colFormLabel">
              Inventory Location
          </label>
          <v-select
              placeholder="Select Inventory Location"
              v-model="location"
              :options="locations"
              label="text"
              :reduce="text => text.id"
          />
        </div>
      </div>
      <div class="col-12 col-lg-12 col-xl-9 col-xxl-9 mt-2">
        <DateQuerySetter
            btnTitle="Go"
            @onClickGo="onClickGo"
        />
      </div>
    </div>

    <div class="card mb-0">
      <div class="d-flex justify-content-between px-2 mb-2">
        <div>
          Product/Service Name: <strong>{{ stockLedgerHeaderData.product_name }}</strong>

          <br>
          Report Period: <strong>{{ stockLedgerHeaderData.report_period }}</strong>
        </div>

        <div>
          <table class="head">
            <tr>
              <td>Opening (Qty) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.opening_stock
                    ? commaFormat(stockLedgerHeaderData.opening_stock)
                    : 0
                  }}</strong>
              </td>
            </tr>
            <tr>
              <td>In (Qty) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.in_quantity
                    ? commaFormat(stockLedgerHeaderData.in_quantity)
                    : 0
                  }}</strong>
              </td>
            </tr>
            <tr>
              <td>Out (Qty) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.out_quantity
                    ? commaFormat(stockLedgerHeaderData.out_quantity)
                    : 0
                  }}</strong>
              </td>
            </tr>
            <tr>
              <td>Closing (Qty) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.closing_stock
                    ? commaFormat(stockLedgerHeaderData.closing_stock)
                    : 0
                  }}</strong>
              </td>
            </tr>
          </table>
        </div>

        <div>
          <table class="head">
            <tr>
              <td>Opening (Value) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.opening_value
                    ? commaFormat(stockLedgerHeaderData.opening_value)
                    : 0.0
                  }}</strong>
              </td>
            </tr>
            <tr>
              <td>In (Value) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.in_amount
                    ? commaFormat(stockLedgerHeaderData.in_amount)
                    : 0.0
                  }}</strong>
              </td>
            </tr>
            <tr>
              <td>Out (Value) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.out_amount
                    ? commaFormat(stockLedgerHeaderData.out_amount)
                    : 0.0
                  }}</strong>
              </td>
            </tr>
            <tr>
              <td>Closing (Value) :</td>
              <td>
                <strong>{{
                    stockLedgerHeaderData.closing_value
                    ? commaFormat(stockLedgerHeaderData.closing_value)
                    : 0.0
                  }}</strong>
              </td>
            </tr>
          </table>
        </div>
      </div>
      <div class="col-12">
        <div class="d-flex justify-content-end px-2">
          <button
              type="button"
              class="btn btn-outline-secondary btn-sm me-1"
              @click="exportMushak"
          >
            Export
          </button>
          <button
              type="button"
              class="btn btn-primary btn-sm"
              @click="printMushak"
          >
            Print
          </button>
        </div>
      </div>
    </div>

    <div class="card mt-1 px-2">
      <StockLedgerTable
          :openingQty="stockLedgerHeaderData.opening_stock"
          :totalInQty="stockLedgerHeaderData.in_quantity"
          :totalOutQty="stockLedgerHeaderData.out_quantity"
          :closingQty="stockLedgerHeaderData.closing_stock"
          :openingValue="stockLedgerHeaderData.opening_value"
          :totalInAmount="stockLedgerHeaderData.in_amount"
          :totalOutAmount="stockLedgerHeaderData.out_amount"
          :closingValue="stockLedgerHeaderData.closing_value"
          :saleValue="stockLedgerHeaderData.sale_value"
          :profitValue="stockLedgerHeaderData.profit_value"
          :balances="stockLedgerData"
          :showOpening="page == 1"
          :showClosing="page == paginationData.last_page"
      />
    </div>
    <div class="px-2">
      <Pagination
          :offset="offset"
          @onPageChange="onPageChange"
      />
    </div>
    <Loader v-if="reportLoading"/>
  </div>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted } from 'vue'
import { useStore }                from 'vuex'
import { useRoute, useRouter }     from 'vue-router'

import handleReport              from '@/services/modules/inventory'
import TitleButton               from '@/components/atom/TitleButton'
import DateQuerySetter           from '@/components/atom/DateQuerySetter'
import StockLedgerTable          from '@/components/molecule/company/stock-ledger/StockLedgerTable'
import Pagination                from "@/components/atom/Pagination";
import Loader                    from '@/components/atom/LoaderComponent'
import handleBusinessesLocations from '@/services/modules/businessesLocations'
import handlePurchase            from '@/services/modules/purchase'
import figureFormatter           from "@/services/utils/figureFormatter";
import AsyncSelect               from "@/components/molecule/input-field/AsyncSelect.vue";
import handleCompany             from "@/services/modules/company";
import mushak62Printer           from "@/services/utils/pdf/mushak62Pdf";

const { fetchBusinessLocationsList } = handleBusinessesLocations()
const { fetchCompanyInfo } = handleCompany();
const { exportToPdfMushak62 } = mushak62Printer();
const { fetchHome, getStockLedgerReport, reportLoading } = handleReport()
const { fetchProductList } = handlePurchase()
const { commaFormat } = figureFormatter();

const showError = inject('showError');

const $store  = useStore()
const $route  = useRoute()
const $router = useRouter()

const productSelect         = ref(null)
const product               = ref(null)
const location              = ref(null)
const stockLedgerData       = ref([])
const stockLedgerHeaderData = ref({})
const locations             = ref([])
const paginationData        = ref({})
const offset                = ref(20)
const productTypes          = ref([])
const productType           = ref('')
const allStockLedger        = ref([])
const allStockLedgerHeader  = ref({})
const companyInfo           = ref({})

const companyId = computed(() => $route.params.companyId)
const start     = computed(() => $route.query.start)
const end       = computed(() => $route.query.end)
const page      = computed(() => $route.query.page)
const token     = computed(() => localStorage.getItem('token'))

watch(productType, () => {
  product.value = null
  if (!productSelect.value) return
  productSelect.value.clear()
})

const setPaginationData = (payload) => $store.commit('setPaginationData', payload)
const resetPagination = () => $store.commit('resetPagination')

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (!start.value && !end.value) return query
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (product.value) query += '&product=' + product.value.id
  query += '&location=' + location.value
  query += '&movement_type=out'
  query += "&page=" + page.value
  query += "&offset=" + offset.value
  return query
}

const setPagination = () => {
  if (!stockLedgerData.value) return;
  setPaginationData({
    records: paginationData.value.total,
    perPage: paginationData.value.per_page,
    currentPage: paginationData.value.current_page,
  });
}

const getStockLedgerData = async (query = '') => {
  try {
    reportLoading.value = true
    let res = await getStockLedgerReport(query)
    if (!res.status) {
      stockLedgerHeaderData.value = {};
      stockLedgerData.value = [];
      showError(res.message)
      return
    }
    stockLedgerHeaderData.value = res.data.headerData;
    stockLedgerData.value = res.data.stockLedgers;
    paginationData.value = res.data.pagination;
    setPagination();
  } catch (err) {
    stockLedgerHeaderData.value = {};
    stockLedgerData.value = [];
    if (!err.response) {
      showError('Something is wrong. Check your connectivity!!')
    }
    if (err.response && err.response.data) {
      showError(err.response.data.message)
      return
    }
  } finally {
    reportLoading.value = false
  }
}

const onClickGo = async () => {
  if (!product.value) {
    showError('Please select a product/service');
    return;
  }

  setPaginationData({});
  let routeQuery = Object.assign({}, $route.query);
  routeQuery.page = 1;
  await $router.push({ path: $route.path, query: routeQuery });
  await getStockLedgerData(getQuery());
}

const loadAllStockLedger = async () => {
  let query = getQuery();
  query += '&is_all=1';
  let res = await getStockLedgerReport(query);
  if (!res.status) {
    showError(res.message);
    return false;
  }
  allStockLedgerHeader.value = res.data.headerData;
  allStockLedger.value = res.data.stockLedgers;
  return true;
}

const printMushakForm = async (printer) => {
  if (!product.value) {
    showError('Please select a product/service');
    return;
  }
  try {
    reportLoading.value = true;
    const loaded = await loadAllStockLedger();
    if (!loaded) return;
    await printer(companyInfo.value, allStockLedgerHeader.value, allStockLedger.value);
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!');
  } finally {
    reportLoading.value = false;
  }
}

const printMushak = () => {
  return printMushakForm(exportToPdfMushak62);
}

const downloadMushakExport = (exportType) => {
  if (!product.value) {
    showError('Please select a product/service');
    return;
  }
  let url = `${process.env.VUE_APP_BASE_URL}/export/inventory/stock-ledger?company_id=${companyId.value}&start_date=${start.value}&end_date=${end.value}&product=${product.value.id}&location=${location.value}&movement_type=out&export_type=${exportType}&_token=${token.value}`
  let a = document.createElement("a")
  a.setAttribute("href", url)
  a.click()
}

const exportMushak = () => {
  downloadMushakExport('mushak_62');
}

const makeProductTypesDropdownOptions = (data) => {
  productTypes.value.push({
    id : '',
    text : 'All'
  });

  for (let key in data.product_type) {
    productTypes.value.push({
      id : key,
      text : data.product_type[key]
    });
  }
  productType.value = 'finished_goods'
}

const getHomeContent = async () => {
  try {
    reportLoading.value = true
    let res = await fetchHome()
    if (!res.status) {
      showError(res.message)
    }
    if (res.status) {
      makeProductTypesDropdownOptions(res.data)
    }
  } catch (err) {
    if (!err.response) {
      showError('Something is wrong. Check your connectivity!!')
    }
    if (err.response) {
      showError(err.response.message)
    }
  } finally {
    reportLoading.value = false
  }
}

const onPageChange = async (pageNo) => {
  let routeQuery = Object.assign({}, $route.query);
  routeQuery.page = pageNo;
  await $router.push({ path: $route.path, query: routeQuery });
  await getStockLedgerData(getQuery());
}

onMounted(() => {
  resetPagination();
  const companyQuery = `?company_id=${companyId.value}`
  const businessLocationRes = fetchBusinessLocationsList(companyQuery)
  const companyRes = fetchCompanyInfo(companyQuery);
  Promise.all([
    businessLocationRes.then(res => {
      if (res.data) {
        locations.value = res.data;
        locations.value.push({
          id  : 'all',
          text: 'All'
        });
      }
    }),
    companyRes.then(res => {
      if (res.status) {
        companyInfo.value = res.data;
      }
    })
  ])
  .then(() => {
    location.value = 'all';
    reportLoading.value = false
  })
  .catch(() => {
    reportLoading.value = false
  })

  getHomeContent();
})
</script>

<style scoped>
.head td {
  text-align: right;
}
</style>
